import {
  WaymarkVideoLayer as PMWaymarkVideoLayer,
  LayerType,
  AssetModificationFits,
  ContentCropping,
  FitFillAlignment,
  ValueKeyframed,
  ContentZoom,
  latest as LatestVDETypes,
} from '@libs/waymark-video/video-descriptor-types';
import { matchObservableObjectChange, ObservableObjectChange } from '@libs/util-ts';

import { ImageAsset, FootageAsset } from '../assets';
import { FootageAssetQuality } from '../assets/locations/FootageAssetLocation';
import { toPrecision } from '../utils/math';

import { BaseLayer } from './BaseLayer';
import { ImageLayer } from './ImageLayer';

export interface FootageTrimData {
  /**
   * The start time of the trimmed content in seconds
   */
  trimStartTime: number;
  /**
   * The duration of the trimmed content in seconds
   * May be undefined if the layer does not have a trim modification set.
   * Should be defaulted to the display duration of the layer.
   */
  trimDuration: number;
}

export class FootageLayer extends BaseLayer<
  PMWaymarkVideoLayer,
  {
    'change:asset': void;
    'change:contentModifications': void;
    'change:adjustments': undefined;
    'change:padding': void;
    'change:backgroundFill': void;
    'change:cropping': void;
    'change:zoom': void;
    'change:fitMode': void;
    'change:fitFillAlignment': void;
    'change:trimData': void;
    'change:muted': boolean;
    'change:masterVolume': number;
    'change:visibility': void;
    'change:fillEffectColor': undefined;
    change: void;
    'change:ignoredReferences': void;
    removed: undefined;
  }
> {
  static layerType: PMWaymarkVideoLayer['ty'] = LayerType.WaymarkVideo;

  /**
   * Creates a new FootageLayer from an ImageLayer.
   *
   * @param imageLayer - The ImageLayer base the new FootageLayer on
   * @param initialFootageAsset - The asset to use for the new FootageLayer
   */
  static fromImageLayer(imageLayer: ImageLayer, initialFootageAsset: FootageAsset): FootageLayer {
    const { rawLayerData: rawImageLayerData, videoDescriptor } = imageLayer;

    const footageAssetID = initialFootageAsset.getID();

    if (
      !(
        'h' in rawImageLayerData &&
        rawImageLayerData.h !== undefined &&
        'w' in rawImageLayerData &&
        rawImageLayerData.w !== undefined
      )
    ) {
      throw new Error('Cannot convert an ImageLayer without dimensions to a FootageLayer');
    }

    // Ensure the new footage asset is added
    videoDescriptor.addAsset(initialFootageAsset);

    // Update the layer data in-place to become a video layer
    const rawLayerData: PMWaymarkVideoLayer = {
      ...rawImageLayerData,
      contentZoom: FootageLayer.DEFAULT_ZOOM,
      // NOTE: The explicit 'h' and 'w' are due to TS not inferring that 'h' and 'w' are
      // guaranteed to be present when simply spreading rawImageLayerData. There may be a better way to do this.
      h: rawImageLayerData.h,
      w: rawImageLayerData.w,
      ty: LayerType.WaymarkVideo,
      refId: footageAssetID,
    };

    const footageLayer = new FootageLayer(rawLayerData, imageLayer.parentComposition);

    /**
     * We noticed on https://waymark.com/videos/zebra_4x5_v2.default/edit that if you swapped the first image to footage that the intro text would lag during the transition.
     * We noticed that the syncToFrame was constantly trying to adjust the playback rate and resulting in weirdness. The solution we came up with was to set the trim data when
     * swapping from Image to Footage. I thought this had to do with timeRemap because of the code path we were taking, but it's possible it's not.
     * @TODO: Look into why this works and see if it's a problem deeper in.
     *
     * Note from Ryan: it looks like the layer in question is malformed due to a template construction issue.
     * The layer's in/out points are 99-147 and its parent comp's are 95-194. This means that the layer's
     * globally resolved in/out points are 194-194, giving us a duration of 0. This template probably
     * is only working out of sheer luck. The template should be fixed but this may also signal that we need
     * to be more defensive against in/out points that shouldn't be possible in the renderer.
     */
    footageLayer.setTrimData(footageLayer.getTrimData());

    return footageLayer;
  }

  /**
   * Replaces this layer with an image layer in the video descriptor, using the provided image asset.
   * Returns the new image layer.
   *
   * @param initialImageAsset - The image asset to use for the new image layer
   * @param applyImageLayerUpdates - An optional function to apply updates to the new image layer before adding it to the composition.
   *                                  This is necessary because things can get a little finicky when applying changes to a layer as it
   *                                  is being added to the composition and set up by the renderer; using this is your best bet to ensure your updates get applied.
   *                                  In an ideal world, this will hopefully be replaced by some sort of more robust system where we can batch video descriptor updates or something.
   */
  changeToImageLayer(
    initialImageAsset: ImageAsset,
    applyImageLayerUpdates?: (newImageLayer: ImageLayer) => void,
  ): ImageLayer {
    const imageLayer = ImageLayer.fromFootageLayer(this, initialImageAsset);
    if (applyImageLayerUpdates) {
      applyImageLayerUpdates(imageLayer);
    }
    this.parentComposition.replaceLayer(this, imageLayer);
    return imageLayer;
  }

  onLayerDataChange(change: ObservableObjectChange): boolean {
    let didChange = false;
    let didContentModificationsChange = false;

    if (super.onLayerDataChange(change)) {
      didChange = true;
    }

    if (matchObservableObjectChange(change, 'refId')) {
      // refId tracks the footage asset ID for this layer
      this.dispatchEvent('change:asset');
      didChange = true;
    }

    if (matchObservableObjectChange(change, 'masterVolume')) {
      // masterVolume tracks the volume for this layer
      this.dispatchEvent('change:masterVolume', this.getMasterVolume());
      didChange = true;
    }

    if (matchObservableObjectChange(change, 'isMuted')) {
      // isMuted tracks the mute state for this layer
      this.dispatchEvent('change:muted', this.getIsMuted());
      didChange = true;
    }

    if (matchObservableObjectChange(change, 'contentAdjustments')) {
      // contentAdjustments tracks the image adjustments for this layer
      this.dispatchEvent('change:adjustments');
      didChange = true;
      didContentModificationsChange = true;
    }

    if (matchObservableObjectChange(change, 'contentCropping')) {
      // contentCropping tracks the cropping for this layer
      this.dispatchEvent('change:cropping');
      didChange = true;
      didContentModificationsChange = true;
    }

    if (matchObservableObjectChange(change, 'contentZoom')) {
      // contentZoom tracks the zoom for this layer
      this.dispatchEvent('change:zoom');
      didChange = true;
      didContentModificationsChange = true;
    }

    if (matchObservableObjectChange(change, 'contentPadding')) {
      // contentPadding tracks the padding for this layer
      this.dispatchEvent('change:padding');
      didChange = true;
      didContentModificationsChange = true;
    }

    if (matchObservableObjectChange(change, 'contentBackgroundFill')) {
      // contentBackgroundFill tracks the background fill for this layer
      this.dispatchEvent('change:backgroundFill');
      didChange = true;
      didContentModificationsChange = true;
    }

    if (matchObservableObjectChange(change, 'contentFit')) {
      // contentFit tracks the fit mode for this layer
      this.dispatchEvent('change:fitMode');
      didChange = true;
      didContentModificationsChange = true;
    }

    if (matchObservableObjectChange(change, 'contentFitFillAlignment')) {
      // contentFitFillAlignment tracks the fit fill alignment for this layer
      this.dispatchEvent('change:fitFillAlignment', undefined);
      didChange = true;
      didContentModificationsChange = true;
    }

    if (
      matchObservableObjectChange(change, 'contentTrimStartTime') ||
      matchObservableObjectChange(change, 'contentTrimDuration') ||
      matchObservableObjectChange(change, 'contentPlaybackDuration') ||
      matchObservableObjectChange(change, 'tm')
    ) {
      // contentTrimStartTime tracks the trim start time for this layer
      this.dispatchEvent('change:trimData');
      didChange = true;
      didContentModificationsChange = true;
    }

    if (didContentModificationsChange) {
      // If any content modifications changed, we'll emit a catch-all change event
      this.dispatchEvent('change:contentModifications');
    }

    return didChange;
  }

  getDimensions() {
    return {
      width: this.rawLayerData.w,
      height: this.rawLayerData.h,
    };
  }

  getAssetID() {
    return this.rawLayerData.refId;
  }

  /**
   * Get the footage asset associated with this layer.
   */
  getAsset() {
    const asset = this.videoDescriptor.getAssetByID(this.getAssetID());
    if (!(asset instanceof FootageAsset)) {
      throw new Error(`${this.toString()}: Asset ${this.getAssetID()} is not a video`);
    }
    return asset;
  }

  /**
   * Get the URL of the footage asset associated with this layer.
   *
   * @param quality - The quality of the footage asset URL to get from the VPS.
   */
  getAssetURL(quality: FootageAssetQuality = FootageAssetQuality.medium) {
    return this.getAsset()?.getURL(quality);
  }

  /**
   * Get the thumbnail URLs of the footage asset associated with this layer.
   */
  getAssetThumbnailURLs() {
    return this.getAsset().getThumbnailURLs();
  }

  /**
   * Sets a new footage asset for this layer.
   * Resets all modifications on the layer to defaults.
   */
  setAsset(asset: FootageAsset) {
    // If we're changing the asset for a footage layer, we're going to make an opinionated decision
    // that all "coordinates" of cropping information (zoom level, cropX, cropY, etc.) should be reset.
    // However, we are going to maintain two cropping properties: contentFit and contentFitFillAlignment.
    // The theory here, is that `contentFit` is most influenced by the properties of the layer instead of the asset. `contentFitFillAlignment`
    // is specific to contentFit: 'fill'; so we're going to maintain that as well.
    const oldAsset = this.getAsset();

    // Ensure the asset is in the manifest
    this.videoDescriptor.addAsset(asset);
    Object.assign(this.rawLayerData, {
      refId: asset.getID(),
      // Reset all modifications to defaults
      contentCropping: FootageLayer.DEFAULT_CROPPING,
      contentZoom: FootageLayer.DEFAULT_ZOOM,
      contentPadding: FootageLayer.DEFAULT_PADDING,
      contentBackgroundFill: FootageLayer.DEFAULT_BACKGROUND_FILL_COLOR,
      contentTrimStartTime: undefined,
      contentTrimDuration: undefined,
      contentPlaybackDuration: undefined,
    });

    // Clean up the old asset if it's no longer in use
    this.videoDescriptor.removeAsset(oldAsset);
  }

  /** ~~ Modifications ~~ */

  static DEFAULT_ADJUSTMENTS: LatestVDETypes.ImageAdjustments = {};

  getVideoAdjustments(): LatestVDETypes.ImageAdjustments {
    return {
      // Spreading to ensure we have a new object which won't accidentally update the
      // video descriptor when mutated
      ...(this.rawLayerData.contentAdjustments ?? ImageLayer.DEFAULT_ADJUSTMENTS),
    };
  }

  setVideoAdjustments(newAdjustments: Partial<LatestVDETypes.ImageAdjustments> | null) {
    this.rawLayerData.contentAdjustments = newAdjustments
      ? {
          // Apply the new adjustments on top of the base defaults
          ...FootageLayer.DEFAULT_ADJUSTMENTS,
          ...newAdjustments,
        }
      : undefined;
  }

  /** contentBackgroundFill modification */
  // Default color is transparent
  static DEFAULT_BACKGROUND_FILL_COLOR = '#FFFFFF00';

  getBackgroundFillColor(): string {
    return this.rawLayerData.contentBackgroundFill ?? FootageLayer.DEFAULT_BACKGROUND_FILL_COLOR;
  }
  setBackgroundFillColor(newColor: string) {
    this.rawLayerData.contentBackgroundFill = newColor;
  }

  /** contentCropping modification */
  static DEFAULT_CROPPING: ContentCropping = {
    x: 0,
    y: 0,
    width: 1,
    height: 1,
  };

  getCropping(): ContentCropping {
    return {
      // Spreading to ensure we have a new object which won't accidentally update the video descriptor when mutated
      ...(this.rawLayerData.contentCropping ?? FootageLayer.DEFAULT_CROPPING),
    };
  }
  setCropping(newCroppingData: Partial<ContentCropping>) {
    if (this.getContentFitMode() !== AssetModificationFits.Fill) {
      throw new Error('Cannot set cropping on a layer with contentFit mode other than "fill"');
    }
    this.rawLayerData.contentCropping = {
      ...this.getCropping(),
      ...newCroppingData,
    };
  }

  /** contentFit modification */
  static DEFAULT_CONTENT_FIT_MODE: `${AssetModificationFits.Crop}` = 'crop';

  getContentFitMode(): `${AssetModificationFits}` {
    return this.rawLayerData.contentFit ?? FootageLayer.DEFAULT_CONTENT_FIT_MODE;
  }
  setContentFitMode(newFitMode: `${AssetModificationFits}`) {
    this.rawLayerData.contentFit = newFitMode;
  }

  /** contentFitFillAlignment modification */
  static DEFAULT_FIT_FILL_ALIGNMENT: `${FitFillAlignment.CenterCenter}` = 'CC';

  getFitFillAlignment(): `${FitFillAlignment}` {
    return this.rawLayerData.contentFitFillAlignment ?? FootageLayer.DEFAULT_FIT_FILL_ALIGNMENT;
  }
  setFitFillAlignment(newAlignment: `${FitFillAlignment}`) {
    this.rawLayerData.contentFitFillAlignment = newAlignment;
  }

  /** contentPadding modification */
  static DEFAULT_PADDING = 0;

  getPadding(): number {
    return this.rawLayerData.contentPadding ?? FootageLayer.DEFAULT_PADDING;
  }
  setPadding(newPadding: number) {
    this.rawLayerData.contentPadding = newPadding;
  }

  /** Trim modification */
  getTrimData(): FootageTrimData {
    return {
      trimStartTime: this.rawLayerData.contentTrimStartTime ?? 0,
      trimDuration: this.rawLayerData.contentTrimDuration ?? this.getLongestSecondsDuration(),
    };
  }

  setTrimData(
    trimDataOrGetter: FootageTrimData | ((currentTrimData: FootageTrimData) => FootageTrimData),
  ) {
    const trimData =
      typeof trimDataOrGetter === 'function'
        ? trimDataOrGetter(this.getTrimData())
        : trimDataOrGetter;

    // Keep values limited to 2 decimal places of precision
    const trimDuration = toPrecision(trimData.trimDuration, 2);
    const trimStartTime = toPrecision(trimData.trimStartTime, 2);

    const layerInPoint = this.rawLayerData.ip;

    const layerFrameDuration = this.getLongestFrameDuration();

    // Set time remapping keyframes on the layer to reflect the trim start/end times
    const timeRemappingKeyframes: ValueKeyframed = {
      a: 1,
      k: [
        {
          s: [trimStartTime],
          e: [trimStartTime + trimDuration],
          t: layerInPoint,
        },
        {
          t: layerInPoint + layerFrameDuration,
        },
      ],
    };

    Object.assign(this.rawLayerData, {
      tm: timeRemappingKeyframes,
      contentTrimDuration: trimDuration,
      contentTrimStartTime: trimStartTime,
      // It's important to remember that while contentTrimDuration is in seconds, contentPlaybackDuration is in frames.
      // Not sure why we didn't just use frames across the board.
      contentPlaybackDuration: Math.min(
        // Use the trim duration if it's less than the layer's display duration,
        // but otherwise cap at the layer's display duration.
        trimDuration * this.videoDescriptor.getFramerate(),
        layerFrameDuration,
      ),
    });
  }

  /** contentZoom modification */
  static DEFAULT_ZOOM: ContentZoom = {
    x: 0.5,
    y: 0.5,
    z: 1,
  };

  getZoom(): ContentZoom {
    return {
      // Spreading to ensure we have a new object which won't accidentally update the video descriptor when mutated
      ...(this.rawLayerData.contentZoom ?? FootageLayer.DEFAULT_ZOOM),
    };
  }
  setZoom(newZoomData: Partial<ContentZoom>) {
    this.rawLayerData.contentZoom = {
      ...this.getZoom(),
      ...newZoomData,
    };
  }

  /** isMuted modification */
  getIsMuted(): boolean {
    return this.rawLayerData.isMuted ?? false;
  }
  setIsMuted(isMuted: boolean) {
    this.rawLayerData.isMuted = isMuted;
  }

  /** masterVolume modification */
  static DEFAULT_MASTER_VOLUME = 1;

  getMasterVolume(): number {
    return this.rawLayerData.masterVolume ?? FootageLayer.DEFAULT_MASTER_VOLUME;
  }
  setMasterVolume(volume: number) {
    this.rawLayerData.masterVolume = volume;
  }

  cleanUp(): void {
    super.cleanUp();
    // Clean up this layer's asset
    try {
      const asset = this.getAsset();
      this.videoDescriptor.removeAsset(asset);
    } catch {
      // Swallow errors if the asset was not found; we don't care if we're cleaning up the layer
    }
  }
}
